import { useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { ApiError, isNotFound } from "../../api/errors";
import { useDeleteStack, useNodes, useRedeployStack, useStack } from "../../api/queries";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { CopyButton } from "../../components/CopyButton";
import { StackStatusPill } from "../../components/StatePill";
import { useFleet } from "../../fleet/FleetContext";
import { useToast } from "../../toast/ToastContext";
import { DownStackDialog } from "./DownStackDialog";
import "../../components/Form.css";
import "./stacks.css";

export function StackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currentId } = useFleet();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();
  const [downOpen, setDownOpen] = useState(false);

  const { data: stack, isPending, isError, error, refetch } = useStack(currentId, id);
  const { data: nodesPage, isPending: nodesPending } = useNodes(currentId);
  const redeployMutation = useRedeployStack(currentId);
  const deleteMutation = useDeleteStack(currentId);

  // Deny-by-default mirrors the server's authz matrix: viewer has stack:view
  // only, operator+ also has create/update/delete. This only hides/disables
  // the controls — the server enforces the real decision on every request
  // (see the 403 handling below for what a stale/incorrect guess here hits).
  const canManage = session?.role !== "viewer";
  // A stack only carries its node's id on the wire (see api/types.ts) — the
  // name is resolved by joining against the nodes list, same as the list
  // page. "—" while that join is still loading, the raw id if the node was
  // since removed.
  const nodeLabel = nodesPending ? "—" : (nodesPage?.items.find((n) => n.id === stack?.nodeId)?.name ?? stack?.nodeId);

  function handleRedeploy() {
    if (!stack) return;
    const stackName = stack.name;
    redeployMutation.mutate(
      { id: stack.id },
      {
        onSuccess: () => pushToast({ title: `Redeploying "${stackName}"…`, tone: "success" }),
        onError: (err) => {
          // A 403 here means the redeploy was refused up front and the stack
          // was left running (the server never tears one down to enforce its
          // own rule) — that message is shown as a persistent banner below
          // instead of a toast, since it's worth more than a few seconds on
          // screen. Anything else still gets the usual toast.
          if (err instanceof ApiError && err.status === 403) return;
          pushToast({
            title: `Couldn't redeploy "${stackName}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  function handleDownConfirm() {
    if (!stack) return;
    const stackName = stack.name;
    deleteMutation.mutate(
      { id: stack.id },
      {
        onSuccess: () => {
          pushToast({ title: `Took "${stackName}" down.`, tone: "success" });
          navigate("/stacks");
        },
        onError: (err) => {
          setDownOpen(false);
          pushToast({
            title: `Couldn't take "${stackName}" down`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  if (isPending) {
    return <LoadingState label="Loading stack…" />;
  }

  if (isError) {
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Stack not found"
          description="It may have been taken down, or the link is out of date. Head back to the stacks list."
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this stack."}
        onRetry={refetch}
      />
    );
  }

  if (!stack) return null;

  const redeployForbidden = redeployMutation.isError && redeployMutation.error instanceof ApiError && redeployMutation.error.status === 403;
  const pendingAction = redeployMutation.isPending || deleteMutation.isPending;

  return (
    <div className="stack-detail">
      <Link to="/stacks" className="stack-detail__back">
        ← Back to stacks
      </Link>

      <div className="stack-detail__header">
        <div className="stack-detail__title-row">
          <h1 className="stack-detail__title">{stack.name}</h1>
          <StackStatusPill status={stack.status} />
        </div>
        {canManage && (
          <div className="stack-detail__actions">
            <Button variant="secondary" disabled={pendingAction} onClick={handleRedeploy}>
              {redeployMutation.isPending ? "Redeploying…" : "Redeploy"}
            </Button>
            <Button variant="danger" disabled={pendingAction} onClick={() => setDownOpen(true)}>
              Down
            </Button>
          </div>
        )}
      </div>

      {redeployForbidden && (
        <p className="form-error" role="alert">
          {redeployMutation.error instanceof Error ? redeployMutation.error.message : "Redeploy was refused."}
        </p>
      )}

      <dl className="stack-detail__meta">
        <div>
          <dt>Target node</dt>
          <dd>{nodeLabel}</dd>
        </div>
        <div>
          <dt>Created by</dt>
          <dd>{stack.createdBy || "—"}</dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{new Date(stack.createdAt).toLocaleString()}</dd>
        </div>
        <div>
          <dt>Updated</dt>
          <dd>{new Date(stack.updatedAt).toLocaleString()}</dd>
        </div>
      </dl>

      <div className="stack-detail__compose-header">
        <h2>Compose file</h2>
        <CopyButton getValue={() => stack.compose} label="Copy compose" />
      </div>
      <pre className="stack-detail__compose-body">
        <code>{stack.compose}</code>
      </pre>

      {canManage && (
        <DownStackDialog
          open={downOpen}
          stackName={stack.name}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDownOpen(false)}
          onConfirm={handleDownConfirm}
        />
      )}
    </div>
  );
}
